/**
 * OnboardingMediaUploadService - Upload service for onboarding background images/videos
 *
 * Provides methods for uploading images and videos to CDN bucket (segmently-assets-{env})
 * via builder-service. Files are served via GLB CDN at /assets/* path.
 *
 * Features:
 * - Images: Automatic resizing (origin + small = 1/3 of original)
 * - Videos: Automatic thumbnail extraction (first frame as PNG)
 *
 * CDN URL format: https://{env}-api.segmently.ai/assets/onboardings/{id}/images/...
 */

import { StorageService, AssetUrl } from './StorageService';

// Re-export AssetUrl for consumers
export type { AssetUrl };

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Image upload result - matches schema structure
 */
export interface ImageUploadResult {
  /** URLs for different resolutions (origin + small with real different URLs) */
  assetUrl: AssetUrl;
  /** Original filename */
  assetName?: string;
}

/**
 * Video upload result - matches schema structure
 */
export interface VideoUploadResult {
  /** URLs for different resolutions (origin = video, thumb = first frame) */
  assetUrl: AssetUrl;
  /** Original filename */
  assetName?: string;
  /** Video thumbnail URL (first frame extracted as PNG) */
  thumbnailUrl: string;
}

/**
 * Upload context for onboarding assets
 */
export interface OnboardingUploadContext {
  /** Project ID */
  projectId: string;
  /** Onboarding ID */
  onboardingId: string;
  /** Version ID */
  versionId: string;
  /** Locale code (e.g., 'en-US', 'ru-RU') */
  locale: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Videos above this size upload DIRECTLY to GCS via signed URLs; smaller ones
 * keep the proven multipart path (server-side ffmpeg thumbnail). The value
 * sits safely below the Cloud Run 32 MiB request cap that silently kills
 * larger multipart bodies at the Google frontend (413 without CORS headers).
 * Single source of truth for the routing decision — lower to 0 to force every
 * video through the direct path.
 */
const DIRECT_UPLOAD_THRESHOLD_BYTES = 20 * 1024 * 1024; // 20MB

/**
 * Sanitize filename for storage path
 * Replaces spaces and special characters with dashes
 */
function sanitizeFilename(filename: string): string {
  // Remove extension first
  const lastDot = filename.lastIndexOf('.');
  const nameWithoutExt = lastDot > 0 ? filename.substring(0, lastDot) : filename;

  return nameWithoutExt
    .replace(/\s+/g, '-') // Replace spaces with dashes
    .replace(/[^a-zA-Z0-9\-_]/g, '') // Remove special characters except dash, underscore
    .toLowerCase();
}

/**
 * Generate CDN path for onboarding background asset
 * Path: onboardings/{onboardingId}/{mediaType}/{locale}_{timestamp}_{filename}
 * Served via GLB at: /assets/onboardings/{onboardingId}/...
 */
function generateCdnPath(
  context: OnboardingUploadContext,
  mediaType: 'images' | 'videos',
  filename: string
): string {
  const timestamp = Date.now();
  const sanitizedFilename = sanitizeFilename(filename);

  return `onboardings/${context.onboardingId}/${mediaType}/${context.locale}_${timestamp}_${sanitizedFilename}`;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

/**
 * OnboardingMediaUploadService - Upload images and videos for onboarding backgrounds
 *
 * Uses StorageService to upload to CDN bucket via builder-service.
 * Builder-service automatically processes uploads:
 * - Images: Creates origin + small (1/3 of original size)
 * - Videos: Creates video + thumbnail (first frame PNG)
 *
 * Files are served via GLB CDN with 24h cache.
 *
 * @example
 * ```typescript
 * const uploadService = new OnboardingMediaUploadService();
 *
 * // Upload image (auto-resized)
 * const imageResult = await uploadService.uploadImage(file, {
 *   projectId: 'proj-123',
 *   onboardingId: 'onb-456',
 *   versionId: 'v-789',
 *   locale: 'en-US'
 * });
 * console.log(imageResult.assetUrl.origin);  // Original size
 * console.log(imageResult.assetUrl.small);   // 1/3 of original
 *
 * // Upload video (auto thumbnail)
 * const videoResult = await uploadService.uploadVideo(file, context);
 * console.log(videoResult.assetUrl.origin);  // Video file
 * console.log(videoResult.thumbnailUrl);     // First frame PNG
 * ```
 */
export class OnboardingMediaUploadService {
  private storageService: StorageService;

  constructor() {
    this.storageService = new StorageService();
  }

  /**
   * Upload image for onboarding background
   *
   * Uploads to CDN bucket via builder-service with automatic resizing:
   * - origin: Original image unchanged
   * - small: 1/3 of original size (proportionally)
   *
   * @param file - Image file to upload
   * @param context - Upload context with project/onboarding/version/locale info
   * @returns Promise resolving to ImageUploadResult with AssetUrl
   */
  async uploadImage(
    file: File,
    context: OnboardingUploadContext
  ): Promise<ImageUploadResult> {
    const cdnPath = generateCdnPath(context, 'images', file.name);

    // Upload to CDN bucket via builder-service (with automatic resizing)
    const result = await this.storageService.uploadImage(cdnPath, file);

    console.log(`[OnboardingMediaUploadService] Uploaded image: origin=${result.assetUrl.origin}, small=${result.assetUrl.small}`);

    return {
      assetUrl: result.assetUrl,
      assetName: file.name,
    };
  }

  /**
   * Upload video for onboarding background
   *
   * Uploads to CDN bucket via builder-service with automatic thumbnail extraction:
   * - origin: Original video file
   * - thumb: First frame extracted as PNG
   *
   * @param file - Video file to upload
   * @param context - Upload context with project/onboarding/version/locale info
   * @returns Promise resolving to VideoUploadResult with AssetUrl and thumbnailUrl
   */
  async uploadVideo(
    file: File,
    context: OnboardingUploadContext,
    options: { onProgress?: (fraction: number) => void } = {}
  ): Promise<VideoUploadResult> {
    const cdnPath = generateCdnPath(context, 'videos', file.name);

    let result: { assetUrl: VideoUploadResult['assetUrl']; thumbnailUrl: string };
    if (file.size > DIRECT_UPLOAD_THRESHOLD_BYTES) {
      // Large file: multipart cannot pass the Cloud Run 32 MiB request cap —
      // PUT straight into GCS via signed URLs (thumbnail extracted client-side).
      try {
        result = await this.storageService.uploadVideoDirect(cdnPath, file, {
          projectId: context.projectId,
          onProgress: options.onProgress,
        });
      } catch (error) {
        // Local dev without impersonated ADC cannot sign — transparent
        // fallback to multipart (works locally: no LB/Cloud Run in front).
        if ((error as Error & { code?: string }).code === 'signing_unavailable') {
          console.warn('[OnboardingMediaUploadService] Signing unavailable, falling back to multipart upload');
          result = await this.storageService.uploadVideo(cdnPath, file);
        } else {
          throw error;
        }
      }
    } else {
      // Small file: existing multipart path (server-side ffmpeg thumbnail).
      result = await this.storageService.uploadVideo(cdnPath, file);
    }

    console.log(`[OnboardingMediaUploadService] Uploaded video: origin=${result.assetUrl.origin}, thumb=${result.thumbnailUrl}`);

    return {
      assetUrl: result.assetUrl,
      assetName: file.name,
      thumbnailUrl: result.thumbnailUrl,
    };
  }

  /**
   * Delete media asset from CDN bucket
   *
   * @param path - CDN path or full URL of the asset to delete
   * @param projectId - Project that owns the onboarding asset; required by the server authorization boundary
   * @returns Promise that resolves when asset is deleted
   */
  async deleteAsset(path: string, projectId: string): Promise<void> {
    // Extract CDN path from full URL if needed
    const cdnPath = path.includes('/assets/')
      ? path.split('/assets/')[1]
      : path;

    if (cdnPath.startsWith('onboardings/')) {
      throw new Error('legacy_onboarding_asset_delete_unsupported');
    }

    await this.storageService.deleteFromAssets(cdnPath, projectId);
  }
}
